import type { ItemProps } from "@react-types/shared";
import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols";
import { flatten, startsWith } from "ramda";
import {
  type JSX,
  type Key,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useCallback,
  useRef,
  useState,
} from "react";
import type { AriaMenuOptions } from "react-aria";
import {
  DismissButton,
  mergeProps,
  useFocus,
  useMenu,
  useMenuItem,
  useMenuSection,
  useSeparator,
  VisuallyHidden,
} from "react-aria";
import { useNavigate } from "react-router";
import { Item, Section, type TreeProps, useTreeState } from "react-stately";
import { Icon, LoadingBar, SymbolIcon, Tooltip, useTooltip } from "widgets";

import newStyle from "./ContextualMenu.module.scss";

enum MenuItemIntent {
  Primary = "primary",
  Danger = "danger",
  Active = "active",
  Inactive = "inactive",
}

const getItems = (items: ReactNode, recursive = true): IMenuSection[] | undefined => {
  if (items == undefined) return undefined;

  const menuProps: IMenuSection[] = [];

  function getItemProps(item: ReactElement) {
    if (item?.props == undefined) return null;

    const { children, ...otherProps } = item.props; // just to extract children
    // first call is Section, then Item. That's why we keep the children at the end
    return recursive
      ? { ...otherProps, items: getItems(Array.isArray(children) ? flatten(children) : children, false) }
      : { ...otherProps, children };
  }

  if (Array.isArray(items)) {
    items
      .filter((i) => i) // remove falsy values (false, undefined, null...)
      .forEach((item) => {
        menuProps.push(getItemProps(item as ReactElement));
      });
  } else if (items) {
    menuProps.push(getItemProps(items as ReactElement));
  }

  return menuProps;
};

function MenuItemRenderer({ item, state, onAction, onClose }) {
  const { props }: { props: IMenuItemProps } = item;

  // Pass all data-* attributes, used for testing purposes
  const dataAttributesProps = {};
  for (const key of Object.keys(props)) {
    if (startsWith("data-", key)) {
      dataAttributesProps[key] = props[key];
    }
  }

  const navigate = useNavigate();
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { menuItemProps, labelProps, descriptionProps } = useMenuItem(
    {
      key: item.key,
      isDisabled: props.isDisabled,
      onAction: props.noAction === true ? undefined : action,
      onClose: props.keepOpen === true ? undefined : onClose,
    },
    state,
    triggerRef,
  );
  const {
    triggerProps,
    tooltipProps,
    arrowProps,
    state: tooltipState,
  } = useTooltip(triggerRef, tooltipRef, { placement: "left", isDisabled: !props.tooltip, offset: 5 });

  function action(...params) {
    if (typeof onAction === "function") {
      onAction(...params);
    }

    if (typeof props.onAction === "function") {
      props.onAction();
    }

    if (typeof props.to === "string") {
      if (startsWith("http", props.to)) {
        window.open(props.to, "_blank");
      } else {
        navigate(props.to);
      }
    }
  }

  const [isFocused, setFocused] = useState(false);
  const { focusProps } = useFocus({ onFocusChange: setFocused });

  return (
    <>
      <div
        {...mergeProps(menuItemProps, triggerProps, focusProps, dataAttributesProps)}
        ref={triggerRef}
        className={clsx(newStyle.item, {
          [newStyle.intent_primary]: props.intent === MenuItemIntent.Primary,
          [newStyle.intent_active]: props.intent === MenuItemIntent.Active,
          [newStyle.intent_danger]: props.intent === MenuItemIntent.Danger,
          [newStyle.intent_inactive]: props.intent === MenuItemIntent.Inactive,
          [newStyle.item_large]: props.isLarge,
          [newStyle.no_style]: props.isOnlyContainer,
          [newStyle.item_focus]: isFocused && !props.isDisabled,
          [newStyle.item_disabled]: props.isDisabled,
        })}
        id={props.id}
      >
        <div className={newStyle.things}>
          {!props.noIcon && (
            <div className={clsx(newStyle.item_icon, { [newStyle.item_icon_large]: props.isLarge })} aria-hidden>
              {props.intent === MenuItemIntent.Active ? (
                <SymbolIcon icon={"check"} />
              ) : props.iconImage ? (
                <img src={props.iconImage.path} width={props.iconImage.width} height={props.iconImage.height} alt="" />
              ) : props.icon ? (
                <Icon icon={props.icon} />
              ) : null}
            </div>
          )}
          <div className={newStyle.title_wrapper}>
            <div
              {...labelProps}
              className={clsx(newStyle.title, labelProps.className, {
                [newStyle.item_label_title_large]: props.isLarge,
              })}
            >
              {item.rendered}
            </div>
            {props.description && (
              <div
                {...descriptionProps}
                className={clsx(newStyle.subtitle, {
                  [newStyle.item_label_subtitle_large]: props.isLarge,
                })}
              >
                {props.description}
              </div>
            )}
          </div>
        </div>
        <div className={newStyle.things}>
          {props.tag && (
            <div className={newStyle.badge}>
              <VisuallyHidden>(</VisuallyHidden>
              {props.tag}
              <VisuallyHidden>)</VisuallyHidden>
            </div>
          )}
          {props.shortcut && (
            <div className={newStyle.shortcut}>
              <VisuallyHidden>(</VisuallyHidden>
              {props.shortcut}++
              <VisuallyHidden>)</VisuallyHidden>
            </div>
          )}
        </div>
      </div>
      {tooltipState.isOpen && (
        <Tooltip domProps={tooltipProps} arrowProps={arrowProps} ref={tooltipRef}>
          {props.tooltip}
        </Tooltip>
      )}
    </>
  );
}

// --- Abstract @react-aria Section and Item ---
interface IMenuSection extends IMenuSectionProps {
  items: PropsWithChildren<IMenuItemProps>[];
}

interface IMenuSectionProps {
  id?: string; // also as the key prop, mandatory for @react-aria/Item, "title" is used if undefined
  title: string; // title or aria-label, mandatory for @react-aria/Section
  hideTitle?: boolean; // switch between title and aria-label
}

interface IMenuItemProps extends Pick<ItemProps<any>, "title" | "textValue" | "aria-label"> {
  id: string; // also as the key prop, mandatory for @react-aria/Item
  // title?: string; // from @react-aria/ItemProps
  isDisabled?: boolean;
  noAction?: boolean; // general MenuDialog action on all items
  keepOpen?: boolean;
  to?: string;
  tooltip?: string | JSX.Element;
  noIcon?: boolean; // TODO ?!? Should be bound to MenuDialog?
  icon?: MaterialSymbol;
  iconImage?: {
    path: string;
    width: number;
    height: number;
  };
  description?: string | JSX.Element;
  tag?: string | JSX.Element;
  shortcut?: string;
  onAction?: Function; // TODO
  intent?: MenuItemIntent;
  /** big icon, width 380px */
  isLarge?: boolean;
  /** no padding */
  isOnlyContainer?: boolean;
}

// === DEPRECATED ===
interface IDropDownMenuProps extends TreeProps<any>, AriaMenuOptions<any> {
  onAction?: (key: Key) => void;
  onClose?: () => void;
  isLoading?: boolean;
}

/**
 * Use useMenu + Menu instead. Still needed with old component until full replacement.
 * @deprecated
 */
function MenuDialog(
  props: PropsWithChildren<Omit<IDropDownMenuProps, "children">> & {
    topWidget?: JSX.Element;
    className?: string;
    footNote?: ReactNode;
  },
): JSX.Element {
  const { topWidget, footNote, className, children, ...innerMenuProps } = props;

  const getItems = useCallback(function getItems(items: ReactNode, recursive = true): IMenuSection[] | undefined {
    if (items == undefined) return undefined;

    const menuProps: IMenuSection[] = [];

    function getItemProps(item: ReactElement) {
      if (item?.props == undefined) return null;

      const { children, ...otherProps } = item.props; // just to extract children
      // first call is Section, then Item. That's why we keep the children at the end
      return recursive
        ? { ...otherProps, items: getItems(Array.isArray(children) ? flatten(children) : children, false) }
        : { ...otherProps, children };
    }

    if (Array.isArray(items)) {
      items
        .filter((i) => !!i) // remove falsy values (false, undefined, null...)
        .forEach((item) => {
          menuProps.push(getItemProps(item as ReactElement));
        });
    } else if (items) {
      menuProps.push(getItemProps(items as ReactElement));
    }

    return menuProps;
  }, []);

  const menuPropsWithSection = {
    ...innerMenuProps,
    // items and children are used instead of @react-aria Section and Item components.
    // See bellow to check how we abstract them through MenuSection and MenuItem components.
    items: getItems(children),
    children: function AriaContextualMenuChildren(section: IMenuSection) {
      return (
        <Section
          key={section.id || section.title}
          title={!section.hideTitle ? section.title : undefined}
          aria-label={section.hideTitle ? section.title : undefined}
          items={section.items}
        >
          {({ children, ...props }) => (
            <Item {...props} key={props.id || props.textValue} textValue={props.textValue || props.id}>
              {children}
            </Item>
          )}
        </Section>
      );
    },
  };

  const state = useTreeState({ ...menuPropsWithSection, selectionMode: "none" });
  const menuRef = useRef(null);
  const { menuProps } = useMenu(menuPropsWithSection, state, menuRef);

  return (
    <div className={clsx(newStyle.menu, className)}>
      <div tabIndex={-1} style={{ outline: "none" }}>
        {topWidget}
      </div>
      <DismissButton onDismiss={innerMenuProps.onClose} />
      <div {...menuProps} ref={menuRef} className={newStyle.menu_list} style={{ maxHeight: props.style?.maxHeight }}>
        <div>
          <LoadingBar isLoading={props.isLoading ?? false} />
        </div>
        {[...state.collection].map((item) => (
          <MenuSectionRenderer
            key={item.key}
            section={item}
            state={state}
            onAction={innerMenuProps.onAction}
            onClose={innerMenuProps.onClose}
          />
        ))}
      </div>
      <DismissButton onDismiss={innerMenuProps.onClose} />
      <div tabIndex={-1} style={{ outline: "none" }}>
        {footNote}
      </div>
    </div>
  );
}

function MenuSectionRenderer({ section, state, onAction, onClose }) {
  const { itemProps, headingProps, groupProps } = useMenuSection({
    heading: section.rendered,
    "aria-label": section["aria-label"],
  });

  const { separatorProps } = useSeparator({
    elementType: "div",
  });

  // If the section is not the first, add a separator element.
  // The heading is rendered inside an <li> element, which contains
  // a <ul> with the child items.
  return (
    <>
      {section.key !== state.collection.getFirstKey() && <div {...separatorProps} className={newStyle.separator} />}
      <div {...itemProps} className={newStyle.section}>
        {section.rendered && (
          <span {...headingProps} className={newStyle.category_title}>
            {section.rendered}
          </span>
        )}
        <div {...groupProps}>
          {[...section.childNodes].map((node) => (
            <MenuItemRenderer key={node.key} item={node} state={state} onAction={onAction} onClose={onClose} />
          ))}
        </div>
      </div>
    </>
  );
}

export { MenuDialog };
